/**
 * Box 6 of the ICS 205-CG, the Communications Unit Leader's Prepared by.
 *
 * The form has one signer and no reviewer box, so there is nothing to chase
 * here. Signing happens on the version bar above, as on every versioned form.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
} from './AssignmentList/ics204SignatureSlots';
import { ICS205_BOX_LABELS } from './ics205BoxLabels';

export type Ics205SignatureBoxProps = {
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

export const Ics205SignatureBox: React.FC<Ics205SignatureBoxProps> = ({
  periodNumber,
}) => {
  const { data: tip } = useFormVersionTip('ICS_205', periodNumber);
  const signature = useMemo(
    () => parseWalaSignatures(tip?.signatures)[0],
    [tip?.signatures]
  );

  return (
    <Ics204CardSection
      boxLabel={ICS205_BOX_LABELS.preparedBy}
      testId="ics205-signature-box"
      className="tw:mt-0"
    >
      {signature ? (
        <div className="tw:grid tw:gap-1 tw:text-sm">
          <Line label="Name" value={signature.name} />
          <Line
            label="Position / Title"
            // The roster seat the signer held, resolved by the server. Older
            // signatures predate it and fall back to the slot they filled.
            value={signature.positionTitle || signature.role}
          />
          <Line label="Signature" value={signature.name} italic />
          <Line
            label="Date / Time"
            value={formatSignatureDateTime(signature.signedAt)}
          />
        </div>
      ) : (
        <div className="tw:text-sm tw:text-muted-foreground">
          Not signed. Create a signed version above to sign this form.
        </div>
      )}
    </Ics204CardSection>
  );
};

export default Ics205SignatureBox;
